import React, { useState } from 'react';
import { useInstrumentForge } from '../InstrumentForgeContext';
import { useInstrumentProfiles } from '@hooks/useInstrumentProfiles';
import ProfileCard from './ProfileCard';
import ProfileBrowser from './ProfileBrowser';
import { RefreshCw, ChevronRight } from 'lucide-react';
import { IconButton } from '../../../components/ui/Button';

const ProfileBlender: React.FC = () => {
  const { state, setBlendConfig, swapProfiles } = useInstrumentForge();
  const { data: instrumentProfiles } = useInstrumentProfiles();
  const { blendConfig } = state;
  const [browserSlot, setBrowserSlot] = useState<'A' | 'B' | null>(null);

  if (!blendConfig) return null;

  const profileA = blendConfig.profileAKey ? instrumentProfiles[blendConfig.profileAKey] : null;
  const profileB = blendConfig.profileBKey ? instrumentProfiles[blendConfig.profileBKey] : null;

  const handleBrowserSelect = (key: string) => {
    if (browserSlot === 'A') {
      setBlendConfig({ profileAKey: key });
    } else if (browserSlot === 'B') {
      setBlendConfig({ profileBKey: key });
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-11 gap-4 items-center">
        <div className="md:col-span-5 space-y-2">
          <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Dominant Profile (A)</label>
          <button
            onClick={() => setBrowserSlot('A')}
            className="w-full flex items-center justify-between bg-slate-950 border-2 border-slate-800 rounded-2xl p-3.5 text-left hover:border-accent/50 transition-all group"
          >
            <span className={`text-sm font-black uppercase tracking-tight ${profileA ? 'text-accent' : 'text-slate-400'}`}>
              {profileA ? profileA.name : 'Select Profile...'}
            </span>
            <ChevronRight size={16} className="text-slate-400 group-hover:text-accent transition-colors" />
          </button>
          {profileA && <ProfileCard profile={profileA} compact />}
        </div>

        <div className="md:col-span-1 flex justify-center">
          <IconButton
            onClick={swapProfiles}
            variant="secondary"
            size="md"
            title="Swap A/B"
            className="rounded-full shadow-lg"
          >
            <RefreshCw size={18} />
          </IconButton>
        </div>

        <div className="md:col-span-5 space-y-2">
          <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Subordinate Profile (B)</label>
          <button
            onClick={() => setBrowserSlot('B')}
            className="w-full flex items-center justify-between bg-slate-950 border-2 border-slate-800 rounded-2xl p-3.5 text-left hover:border-accent/50 transition-all group"
          >
            <span className={`text-sm font-black uppercase tracking-tight ${profileB ? 'text-accent' : 'text-slate-400'}`}>
              {profileB ? profileB.name : 'Select Profile...'}
            </span>
            <ChevronRight size={16} className="text-slate-400 group-hover:text-accent transition-colors" />
          </button>
          {profileB && <ProfileCard profile={profileB} compact />}
        </div>
      </div>

      <div className="bg-slate-900/60 p-6 rounded-2xl border-l-4 border-l-accent space-y-4">
        <div className="flex justify-between items-center text-[11px] font-black uppercase tracking-widest">
          <span className="text-accent">Profile A: {blendConfig.blendRatio}%</span>
          <span className="text-slate-400">Blend Ratio</span>
          <span className="text-blue-400">Profile B: {100 - blendConfig.blendRatio}%</span>
        </div>
        <input
          type="range"
          min="0"
          max="100"
          value={blendConfig.blendRatio}
          onChange={(e) => setBlendConfig({ blendRatio: parseInt(e.target.value) })}
          className="w-full h-2 bg-slate-800 rounded-lg appearance-none cursor-pointer accent-accent"
        />
        <p className="text-center text-[11px] text-slate-400 italic">
          {blendConfig.blendRatio > 70 ? `Heavy ${profileA?.name} foundation with ${profileB?.name} character.` :
           blendConfig.blendRatio < 30 ? `Heavy ${profileB?.name} foundation with ${profileA?.name} character.` :
           `Balanced hybrid of ${profileA?.name} and ${profileB?.name} aesthetics.`}
        </p>
        <div className="flex gap-2 justify-center pt-1">
          {[
            { label: 'Pure A', value: 100 },
            { label: '75/25', value: 75 },
            { label: '50/50', value: 50 },
            { label: '25/75', value: 25 },
            { label: 'Pure B', value: 0 },
          ].map(preset => (
            <button
              key={preset.label}
              type="button"
              onClick={() => setBlendConfig({ blendRatio: preset.value })}
              className={`px-3 py-1.5 rounded-lg text-[11px] font-black uppercase tracking-wider transition-all ${
                blendConfig.blendRatio === preset.value
                  ? 'bg-accent text-slate-950 shadow-sm'
                  : 'bg-slate-800 text-slate-400 hover:text-slate-200 hover:bg-slate-700'
              }`}
            >
              {preset.label}
            </button>
          ))}
        </div>
      </div>

      {/* Profile Browser modal */}
      <ProfileBrowser
        isOpen={browserSlot !== null}
        onClose={() => setBrowserSlot(null)}
        onSelect={handleBrowserSelect}
        selectedKey={browserSlot === 'A' ? blendConfig.profileAKey : blendConfig.profileBKey}
        excludeKey={browserSlot === 'A' ? blendConfig.profileBKey : blendConfig.profileAKey}
        contextLabel={browserSlot === 'A' ? 'Select Dominant Profile (A)' : 'Select Subordinate Profile (B)'}
      />
    </div>
  );
};

export default ProfileBlender;
